import React from 'react';
import { Text } from '@airwallex/typography';
import { useTranslation } from 'react-i18next';
import { HStack } from '@airwallex/stacks';
import Tooltip from '@airwallex/tooltip';
import { IconChevronDown, IconChevronUp, IconInformationalOutline } from '@airwallex/glyphs';
import { DiscountInfo } from '@/types/discount';
import { buildDiscountLabel } from '@/utils/discount';
import { formatMoney } from '@/utils/formatPayment';
import { LineItemsShowMoreButton } from './style';

export type LineItemColKey = 'product' | 'quantity' | 'unitPrice' | 'creditSubtotal' | 'discount' | 'amount' | 'tax';

export const ShowMoreRow: React.FC<{
  showAllItems: boolean;
  setShowAllItems: (v: boolean) => void;
}> = ({ showAllItems, setShowAllItems }) => {
  const { t } = useTranslation();
  return (
    <LineItemsShowMoreButton
      iconAfter={showAllItems ? IconChevronUp : IconChevronDown}
      onClick={() => setShowAllItems(!showAllItems)}
    >
      {showAllItems ? t('invoice.showLessLineItems') : t('invoice.showMoreLineItems')}
    </LineItemsShowMoreButton>
  );
};

export const DiscountColumnTooltip: React.FC<{
  discounts?: DiscountInfo[];
  hasLineItemDiscounts: boolean;
  currency: string;
}> = ({ discounts, hasLineItemDiscounts, currency }) => {
  const { t } = useTranslation();
  if (!discounts || discounts.length === 0) return null;
  return (
    <>
      <div>
        <strong>
          {hasLineItemDiscounts ? t('invoice.discountTooltip.both') : t('invoice.discountTooltip.invoiceOnly')}
        </strong>
      </div>
      {discounts.map((discount) => (
        <div key={discount.discount.id}>
          {buildDiscountLabel(discount, t)}: {`-${formatMoney(discount.amount, currency)}`}
        </div>
      ))}
    </>
  );
};

/** `as="span"` — line-items header wrapper uses Typography; avoid invalid `<p>` inside `<p>`. */
export const HeaderText = ({
  children,
  exclTax,
  tooltip,
  testId,
}: {
  children: React.ReactNode;
  exclTax?: boolean;
  tooltip?: React.ReactNode;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const title = (
    <Text as="span" variant="bodyBold" color="primary" title={children as string}>
      {children}
    </Text>
  );
  return (
    <div data-test={testId}>
      {exclTax ? (
        <HStack align={exclTax ? 'RIGHT' : 'LEFT'} space="4px" style={{ alignItems: 'center' }}>
          {title}
          {tooltip ? (
            <Tooltip body={<IconInformationalOutline size={14} />} position="top" style={{ lineHeight: 1 }}>
              {tooltip}
            </Tooltip>
          ) : null}
        </HStack>
      ) : (
        title
      )}
      {exclTax && (
        <Text as="span" variant="hint" color="secondary" style={{ display: 'block' }}>
          {t('invoice.exclTax')}
        </Text>
      )}
    </div>
  );
};

export const getCommonWidth = (hasDiscount: boolean) => (hasDiscount ? 90 : 120);
